// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: App settings & contact channels  (the site's contact details: staff-published values over the built-in ones)
// useAppSettings() → the public settings (null until loaded / on error, so callers always fall back to the built-in values). useContactInfo() → phone, hours, address and email with the built-in
// data/legalContact values as the fallback for every empty field; it also points the WhatsApp links (data/whatsappContext) at the published number.
// Neighbours: utils/appSettings, pages/Contact, pages/HelpCenter, components/Footer, data/legalContact, data/whatsappContext.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_HREF, REGISTERED_ADDRESS, SUPPORT_HOURS } from "../data/legalContact";
import { setWhatsAppNumber } from "../data/whatsappContext";
import { fetchPublicAppSettings, type PublicAppSettings } from "../utils/appSettings";

export function useAppSettings(): PublicAppSettings | null {
  const [settings, setSettings] = useState<PublicAppSettings | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchPublicAppSettings().then((s) => !cancelled && setSettings(s), () => undefined);
    return () => { cancelled = true; };
  }, []);
  return settings;
}

export function useContactInfo() {
  const s = useAppSettings();
  // Set during render (an idempotent module value), so links built later in THIS render already use the published number.
  setWhatsAppNumber(s?.contact.whatsapp || null);
  const phone = s?.contact.supportPhone || CONTACT_PHONE_DISPLAY;
  return {
    phoneDisplay: phone,
    phoneHref: s?.contact.supportPhone ? phone.replace(/[^\d+]/g, "") : CONTACT_PHONE_HREF,
    hours: s?.contact.supportHours || SUPPORT_HOURS,
    address: s?.contact.address || REGISTERED_ADDRESS,
    email: s?.emails.general || CONTACT_EMAIL,
    grievanceEmail: s?.emails.grievance || "",
  };
}
